import React, { useLayoutEffect } from "react";
// import { useTranslation } from "react-i18next";
import SplitType from "split-type";
import gsap from "gsap";
import OtherProjects from "../../components/Other-Projects/otherProjects";
import ProjectNav from "../../components/Project-Nav/projectNav";

export default function TerapoolProject() {
  // const { t } = useTranslation();

  useLayoutEffect(() => {
    const text = new SplitType(".words1", { types: "chars" });
    const textA = SplitType.create(".sentence1", { types: "words" });
    const textB = SplitType.create(".line1", { types: "lines" });

    gsap.fromTo(
      text.chars,
      { y: 0, opacity: 0 },
      { y: -12, opacity: 1, stagger: 0.03 }
    );
    gsap.fromTo(
      textA.words,
      { y: 0, opacity: 0, delay: 0.5 },
      { y: -12, opacity: 1, stagger: 0.06 }
    );
    gsap.fromTo(
      textB.lines,
      { y: 0, opacity: 0, delay: 0.5 },
      { y: -12, opacity: 1, stagger: 0.1 }
    );

    gsap.fromTo(
      ".box1",
      { y: 0, opacity: 0, delay: 1 },
      { y: -20, opacity: 2, duration: 1 }
    );

    gsap.config({
      force3D: false,
      nullTargetWarn: false,
      trialWarn: false,
    });
  });

  return (
    <div className="project-section case-study terapool-project container mt-120">
      <div className="project-hero-card bg-secondary border-exlight radius-20 w-100 p-40 sm:p-20 xs:p-20 box1">
        <div className="project-hero-title mb-20 sm:mb-12 xs:mb-8">
          <h1 className="f-40 xs:f-18 xs:l-h-24 sm:f-32 l-h-40 f-w-600 text-primary words1">
            Terapool
          </h1>
          <p className="f-16 xs:f-12 xs:l-h-20 sm:f-14 l-h-30 f-w-300 text-primary mt-8 sentence1">
            <span>Web3 · Crypto Fundraising · Web + Mobile · TalMare Technologies · 4 months</span>
          </p>
        </div>
      </div>

      <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary mt-24 mb-20 mw-680 line1">
        A blockchain-based fundraising platform supporting ETH, USDT, and
        BNB token contributions. I designed the full experience across four
        user roles — super admin, admin, fund manager, and contributor —
        and also built the frontend in React.js. The main challenge was
        making Web3 contribution flows feel approachable for people who
        weren't already crypto-native.
      </p>

      <div className="cs-pills">
        <span className="cs-pill">Web3 / Blockchain</span>
        <span className="cs-pill">ETH · USDT · BNB</span>
        <span className="cs-pill">Designer + Dev</span>
        <span className="cs-pill">TalMare · 4 months</span>
      </div>

      <div className="cs-stats">
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#4ade80" }}>4</div>
          <div className="cs-stat-l">User roles designed</div>
        </div>
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#fbbf24" }}>3</div>
          <div className="cs-stat-l">Token types</div>
        </div>
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#38bdf8" }}>✓</div>
          <div className="cs-stat-l">Platform shipped</div>
        </div>
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#a78bfa" }}>🔐</div>
          <div className="cs-stat-l">Metamask + Trust Wallet</div>
        </div>
      </div>

      <div className="mb-32">
        <div className="cs-lbl">The product</div>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary line1">
          Terapool is a Web3 fundraising platform where project creators
          raise crypto contributions in ETH, USDT, and BNB. Contributors
          discover projects, connect their wallets, and contribute tokens.
          Four different roles — super admin, admin, fund manager,
          contributor — each with their own part of the platform to
          manage.
        </p>
      </div>

      <div className="mb-32">
        <div className="cs-lbl">My role</div>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary line1">
          UI/UX Designer and Frontend Developer. User research, color
          system, typography, IA, wireframes, high-fidelity design across
          web and mobile, role-based access design, wallet connection
          flows — and React.js implementation alongside the blockchain dev
          team.
        </p>
      </div>

      <div className="mb-8">
        <div className="cs-lbl">Research</div>
        <h3 className="f-20 l-h-28 f-w-600 text-primary mb-12 sentence1">
          The onboarding problem every Web3 platform had
        </h3>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary mb-12 line1">
          Reviewed Gitcoin, Polkastarter, Seedify, and other Web3 launchpad
          platforms. The onboarding problem was consistent everywhere —
          wallet connection flows assumed crypto knowledge that a large
          portion of potential contributors simply didn't have. Most
          platforms felt built for people who already knew what they were
          doing.
        </p>
        <div className="cs-quote">
          <p>
            "I wanted to contribute to a project I believed in but I gave
            up trying to connect my wallet. It kept asking me to do things
            I didn't understand and there was no way to tell if I was
            doing it right."
          </p>
          <span>— Research interview, potential contributor</span>
        </div>
      </div>

      <div className="mt-32 xs:mt-40 sm:mt-40 flex xs:flex-column">
        <div className="cs-img bg-secondary border-exlight radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 mr-15 xs:mr-0 box1 terapool-img1"></div>
        <div className="cs-img bg-secondary border-exlight radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 ml-15 xs:ml-0 xs:mt-20 box1 terapool-img2"></div>
      </div>

      <div className="mt-32 mb-8">
        <div className="cs-lbl">Pain points</div>
        <div className="cs-pains">
          <div className="cs-pain">
            <span className="cs-tag h">Critical</span>
            <div className="cs-pain-text">
              Wallet connection flows were technical and text-heavy —
              showing raw addresses and confirmation prompts with no
              plain-language explanation of what each step meant
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag h">Critical</span>
            <div className="cs-pain-text">
              No feedback during contribution processing — contributors
              couldn't tell if a transaction was processing, pending, or
              failed until several minutes passed
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag h">Critical</span>
            <div className="cs-pain-text">
              Role boundaries were invisible in the UI — fund managers and
              contributors often saw the same interface with different
              permissions silently applied, creating constant confusion
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag m">Medium</span>
            <div className="cs-pain-text">
              Project discovery was flat — no way to tell which projects
              were gaining momentum, nearing deadline, or had curatorial
              backing
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag m">Medium</span>
            <div className="cs-pain-text">
              Mobile experience was absent or broken on most Web3 platforms
              despite mobile being the primary access point for a growing
              segment of participants
            </div>
          </div>
        </div>
        <div className="cs-quote">
          <p>
            The wallet is the login. If connecting it feels like a
            technical procedure, you've already lost most of your
            potential contributors.
          </p>
        </div>
      </div>

      <div className="mt-32 mb-8">
        <div className="cs-lbl">Design decisions</div>
        <div className="cs-two-col">
          <div className="cs-card box1">
            <h4>Wallet connection as step one</h4>
            <p>
              Made wallet connection the first step — not buried in
              settings. Plain-language instructions with visual feedback
              at every stage. Designed separate flows for Metamask and
              Trust Wallet — not one generic flow applied to both.
            </p>
          </div>
          <div className="cs-card box1">
            <h4>Role-based interfaces, not silent permissions</h4>
            <p>
              Each role — super admin, admin, fund manager, contributor —
              has a distinct interface entry point. Users always know
              which role they're in and what's available to them. Not the
              same dashboard with things quietly disabled.
            </p>
          </div>
          <div className="cs-card box1">
            <h4>Mobile-first for contributors</h4>
            <p>
              Admin and fund manager flows were desktop-primary. All
              contributor flows — browse, connect, contribute — were
              designed mobile-first. That's where the target audience was
              most likely to encounter the platform.
            </p>
          </div>
          <div className="cs-card box1">
            <h4>Dark with high-contrast accents</h4>
            <p>
              Deep dark backgrounds with vibrant green and yellow. Web3
              energy without the neon chaos that makes most crypto
              platforms feel untrustworthy. Large typography for
              readability in different lighting conditions.
            </p>
          </div>
        </div>
      </div>

      <div className="cs-img bg-secondary radius-20 minh-600 sm:minh-450 xs:minh-200 mt-32 box1 terapool-img3"></div>
      <div className="cs-img bg-secondary radius-20 minh-600 sm:minh-450 xs:minh-200 mt-32 box1 terapool-img4"></div>

      <div className="mt-32 xs:mt-40 sm:mt-40 flex xs:flex-column">
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 mr-15 xs:mr-0 box1 terapool-img5"></div>
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 ml-15 xs:ml-0 xs:mt-20 box1 terapool-img6"></div>
      </div>

      <div className="mt-32 mb-32">
        <div className="cs-lbl">Handoff &amp; build</div>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary line1">
          Documented component states for all four roles and handed them
          off alongside the Metamask and Trust Wallet integration
          requirements. Each role's permissions were made explicit before
          the build started — nothing left to developer interpretation.
          Implemented the frontend in React.js as well, which made the
          blockchain integration smoother from day one.
        </p>
      </div>

      <div className="mb-8">
        <div className="cs-lbl">Outcomes</div>
        <div className="cs-outcomes">
          <div className="cs-outcome">
            <div className="ico">🚀</div>
            <h4>Platform shipped</h4>
            <p>
              All milestones completed. Web3 fundraising platform launched
              and passed beta with real users contributing real tokens.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">👥</div>
            <h4>4 roles delivered</h4>
            <p>
              Super admin, admin, fund manager, contributor — each with
              its own interface reflecting their specific access and task
              set.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">🔐</div>
            <h4>Dual wallet flows</h4>
            <p>
              Metamask and Trust Wallet designed with separate, distinct
              flows — plain-language guidance tailored to how each wallet
              works.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">📱</div>
            <h4>Web + mobile</h4>
            <p>
              Full coverage across both — contributor flows specifically
              designed for mobile-first interaction.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">🔁</div>
            <h4>Beta feedback incorporated</h4>
            <p>
              Every module updated based on real user feedback — including
              complex contribution management flows.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">💻</div>
            <h4>Built it too</h4>
            <p>
              React.js implementation as both designer and developer —
              clean components ready from day one of build.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-32">
        <div className="cs-lbl">Reflection</div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>Web3 UX is fundamentally a trust problem.</strong>{" "}
            Every interaction — connecting a wallet, confirming a
            transaction — requires trusting a system you can't see.
            Designing for Web3 means building trust signals into every
            screen, not just the disclaimer text.
          </p>
        </div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>Role-based design is harder than it looks.</strong>{" "}
            Most UX work designs for one user type. Terapool had four,
            each with different permissions and mental models. Getting the
            role boundaries into the visual language — not just the
            backend logic — was the most complex IA challenge I'd worked
            on.
          </p>
        </div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>Being designer and developer made the product better.</strong>{" "}
            Implementing my own designs gave me direct insight into how
            wallet connections actually worked technically. That
            understanding changed several design decisions I wouldn't have
            made otherwise.
          </p>
        </div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>If I did this again:</strong> More research with
            non-crypto-native users specifically. Our sessions skewed
            toward people who already owned crypto — the users we were
            trying to make the platform accessible to were
            underrepresented.
          </p>
        </div>
      </div>

      <div className="project-bottom-nav mt-60 mb-100 flex justify-between align-center box1">
        <div className="flex xs:justify-center">
          <OtherProjects active="terapool" />
        </div>
        <ProjectNav current="terapool" />
      </div>
    </div>
  );
}
